import { createOrderIndexAndFixup } from '@gitbook/util-orderindex';
import { assert } from '@gitbook/util-ts';

import {
    generateTagSlug,
    getTagGroupChildren,
    isTagGroupDescendant,
    isValidTagSlug,
    normalizeTagLabel,
    TagValidationError,
} from '../../tags';
import type { Revision, RevisionTagGroup, RevisionTagGroups, RevisionTags } from '../../types';
import { addUserEdits, createEdits, mutations, yToJSON } from '../../utils';
import { normalizeTagParentsInRevision } from './tagParents';

export type TagGroupPlacement = { into?: string | null; at?: number };

/** Insert a group using the same validation for realtime and API edits. */
export function insertTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    input: { slug?: string; label: string } & TagGroupPlacement,
    authors: string[] = []
): void {
    const label = normalizeTagLabel(input.label);
    const slug = input.slug ?? generateTagSlug(label);
    assert(label && isValidTagSlug(slug), new TagValidationError('Invalid tag group name or slug'));
    const groups = getGroups(revision);
    assert(
        !Object.hasOwn(groups, slug),
        new TagValidationError('Tag group with same slug already exists')
    );
    const parent = input.into ?? undefined;
    validatePlacement(groups, slug, parent, input.at);
    let target = mutations.get(revision, 'tagGroups');
    if (!target) {
        mutations.set(revision, 'tagGroups', {});
        target = mutations.get(revision, 'tagGroups')!;
    }
    const orderIndex = createGroupOrderIndex(target, groups, slug, parent, input.at, authors);
    mutations.set(target, slug, {
        slug,
        label,
        ...(parent !== undefined ? { parent } : {}),
        orderIndex,
        edits: createEdits(authors),
    });
}

export function updateTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    update: { label?: string } & TagGroupPlacement,
    authors: string[] = []
): void {
    const groups = getGroups(revision);
    assert(Object.hasOwn(groups, slug), new TagValidationError('Tag group does not exist'));
    const existing = groups[slug];
    const parent = update.into === undefined ? existing.parent : (update.into ?? undefined);
    validatePlacement(groups, slug, parent, update.at);
    const label = update.label === undefined ? existing.label : normalizeTagLabel(update.label);
    assert(label, new TagValidationError('Invalid tag group name'));
    const target = mutations.get(revision, 'tagGroups')!;
    const group = mutations.get(target, slug);
    if (parent !== existing.parent || update.at !== undefined) {
        mutations.set(
            group,
            'orderIndex',
            createGroupOrderIndex(target, groups, slug, parent, update.at, authors)
        );
    }
    if (parent === undefined) mutations.del(group, 'parent');
    else mutations.set(group, 'parent', parent);
    mutations.set(group, 'label', label);
    if (authors.length) {
        mutations.set(group, 'edits', addUserEdits(mutations.get(group, 'edits'), authors));
    }
}

export function removeTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string
): void {
    const groups = getGroups(revision);
    assert(Object.hasOwn(groups, slug), new TagValidationError('Tag group does not exist'));
    const tags: RevisionTags = yToJSON(mutations.get(revision, 'tags')) ?? {};
    assert(
        !Object.values(groups).some((group) => group.parent === slug) &&
            !Object.values(tags).some((tag) => tag.group === slug),
        new TagValidationError('Move all tags and subgroups before deleting this group')
    );
    mutations.del(mutations.get(revision, 'tagGroups')!, slug);
}

export function setTagGroupInRevision(
    revision: mutations.MutableData<Revision>,
    slug: string,
    group: string | null,
    authors: string[] = []
): void {
    const tags = mutations.get(revision, 'tags');
    const tag = tags && mutations.get(tags, slug);
    assert(tag && Object.hasOwn(yToJSON(tags), slug), new TagValidationError('Tag does not exist'));
    assert(
        group === null || Object.hasOwn(getGroups(revision), group),
        new TagValidationError('Tag group does not exist')
    );
    if (group === null) mutations.del(tag, 'group');
    else mutations.set(tag, 'group', group);
    if (authors.length) {
        mutations.set(tag, 'edits', addUserEdits(mutations.get(tag, 'edits'), authors));
    }

    // Child tags stay in their parent's group: a parent brings its children along, and a child
    // moved elsewhere leaves its parent.
    const all: RevisionTags = yToJSON(tags);
    const parent = all[slug].parent;
    if (parent !== undefined && all[parent]?.group !== all[slug].group) {
        mutations.del(tag, 'parent');
    }
    for (const [childSlug, child] of Object.entries(all)) {
        if (child.parent !== slug) continue;
        const childTag = mutations.get(tags, childSlug)!;
        if (group === null) mutations.del(childTag, 'group');
        else mutations.set(childTag, 'group', group);
        if (authors.length) {
            mutations.set(
                childTag,
                'edits',
                addUserEdits(mutations.get(childTag, 'edits'), authors)
            );
        }
    }
}

/**
 * Clean up category data that concurrent edits or a merge left inconsistent, without deleting
 * tags: a group whose parent is gone or would create a loop moves to the top level, and a tag
 * whose category is gone becomes uncategorized. Returns whether anything changed.
 */
export function normalizeTagGroupsInRevision(revision: mutations.MutableData<Revision>): boolean {
    const target = mutations.get(revision, 'tagGroups');
    let changed = false;
    // Drop malformed entries first: everything below reads their fields.
    if (target) {
        for (const [slug, group] of Object.entries(
            (yToJSON(target) ?? {}) as Record<string, Partial<RevisionTagGroup> | null>
        )) {
            if (
                !group ||
                typeof group !== 'object' ||
                typeof group.label !== 'string' ||
                !group.label.trim() ||
                typeof group.orderIndex !== 'string'
            ) {
                mutations.del(target, slug);
                changed = true;
            } else {
                const stored = mutations.get(target, slug)!;
                if (group.slug !== slug) {
                    mutations.set(stored, 'slug', slug);
                    changed = true;
                }
                if (!group.edits || typeof group.edits !== 'object') {
                    mutations.set(stored, 'edits', createEdits([]));
                    changed = true;
                }
                // `hasOwn` below would coerce a number to a matching slug.
                if (group.parent !== undefined && typeof group.parent !== 'string') {
                    mutations.del(stored, 'parent');
                    changed = true;
                }
            }
        }
    }
    const groups = getGroups(revision);
    for (const slug of Object.keys(groups).sort()) {
        const group = groups[slug];
        if (
            group.parent !== undefined &&
            (!Object.hasOwn(groups, group.parent) ||
                isTagGroupDescendant(groups, slug, group.parent))
        ) {
            delete group.parent;
            mutations.del(mutations.get(target!, slug), 'parent');
            changed = true;
        }
    }
    const tags = mutations.get(revision, 'tags');
    if (tags) {
        for (const tag of mutations.values(tags)) {
            const group = mutations.get(tag, 'group');
            if (
                group !== undefined &&
                (typeof group !== 'string' || !Object.hasOwn(groups, group))
            ) {
                mutations.del(tag, 'group');
                changed = true;
            }
        }
    }
    // Clearing a group can split a parent from its children, so parents are repaired after.
    if (normalizeTagParentsInRevision(revision)) changed = true;
    return changed;
}

/** A JSON copy of the groups, each carrying the slug it is keyed by. */
function getGroups(revision: mutations.MutableData<Revision>): RevisionTagGroups {
    const groups: RevisionTagGroups = yToJSON(mutations.get(revision, 'tagGroups')) ?? {};
    return Object.fromEntries(
        Object.entries(groups).map(([slug, group]) => [slug, { ...group, slug }])
    );
}

function validatePlacement(
    groups: RevisionTagGroups,
    slug: string,
    parent: string | undefined,
    at: number | undefined
) {
    assert(parent !== '', new TagValidationError('Invalid parent tag group'));
    assert(
        !parent || Object.hasOwn(groups, parent),
        new TagValidationError('Parent tag group does not exist')
    );
    assert(
        !parent || !isTagGroupDescendant(groups, slug, parent),
        new TagValidationError('Tag groups cannot contain a cycle')
    );
    assert(
        at === undefined || (Number.isInteger(at) && at >= 0),
        new TagValidationError('Invalid tag group position')
    );
}

function createGroupOrderIndex(
    target: mutations.MutableData<RevisionTagGroups>,
    groups: RevisionTagGroups,
    slug: string,
    parent?: string,
    at?: number,
    authors: string[] = []
) {
    const siblings = getTagGroupChildren(groups, parent).filter((group) => group.slug !== slug);
    const index = Math.min(at ?? siblings.length, siblings.length);
    const { index: orderIndex, fixAfterIndex } = createOrderIndexAndFixup({
        before: siblings[index - 1]?.orderIndex,
        after: siblings[index]?.orderIndex,
        nextSiblingIndex: siblings[index + 1]?.orderIndex,
    });
    if (fixAfterIndex) {
        const sibling = mutations.get(target, siblings[index].slug);
        mutations.set(sibling, 'orderIndex', fixAfterIndex);
        if (authors.length) {
            mutations.set(sibling, 'edits', addUserEdits(mutations.get(sibling, 'edits'), authors));
        }
    }
    return orderIndex;
}
